'use client';
import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useSelector } from 'react-redux';
import SearchInput from '@/components/@generalComponents/SearchInput';
import MessagesSlider from '@/components/@generalComponents/MessagesSlider';
import CategoryCards from '@/components/Home/CategoryCards';
import BrandCards from '@/components/Home/BrandCards';
import sendNetworkReq from '@/utils/sendNetworkReq';
import { useMediaQuery, useTheme } from '@mui/material';
import BrandSlider from '@/components/@generalComponents/BrandSlider';
import SwiperCard from '../@generalComponents/SwiperCard';
import { applyPaywall, shouldShowPaywall } from '@/utils/paywall';
import { UserAuth } from '@/app/context/AuthContext';

const HomePage = ({ categories: cat, brands }) => {
	const theme = useTheme();
	const isMd = useMediaQuery(theme.breakpoints.down('md'));
	const { user, isPro, authReady } = UserAuth();
	const router = useRouter();

	// Signed-in visitors land on Explore as soon as Firebase reports a user; we
	// don't wait on the backend tier sync, which can be slow or fail. Guests stay
	// on the server-rendered homepage (for SEO).
	useEffect(() => {
		if (authReady && user) router.replace('/explore');
	}, [authReady, user, router]);

	const [messageSections, setMessageSections] = useState();

	// const reduxCategories = useSelector(
	// 	(state) => state.categoryReducer.allCategories
	// );
	const categories = cat;
	const knownBrands = useSelector((state) => state.brandReducer.knownBrands);
	const brandsToShow = brands || knownBrands;

	const categoriesToShow = [
		// '6519604594c1b61a4d62db60',
		// '6519604594c1b61a4d62db64',
		// '6519604594c1b61a4d62db66',
	];
	// Refetch when the signed-in user or PRO status changes so the backend
	// returns the right tier.
	useEffect(() => {
		getMessages();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user?.uid, isPro]);

	// Ignores responses from a fetch that a newer one (tier change) superseded.
	const requestIdRef = useRef(0);

	const getMessages = async () => {
		const myRequestId = ++requestIdRef.current;
		const data = [];
		const pageSize = 50;
		let allMessages = [];
		// Guests only get their newest messages from page 0 (the backend returns nothing
		// for later pages), so they never request page 1.
		const maxPages = user ? 2 : 1;

		try {
			for (let page = 0; page < maxPages; page++) {
				const response = await sendNetworkReq({
					method: 'GET',
					url: `messages/search?page=${page}&size=${pageSize}&query=sale`,
				});

				if (requestIdRef.current !== myRequestId) return; // stale
				if (!response || response.error) {
					console.error('🚨 Error fetching messages:', response?.error);
					break;
				}
				allMessages = [...allMessages, ...response.messages];

				if (!response?.pagination?.nextPage) break;
			}

			// Paywall first, then split across the two rows, so a guest's
			// messages are shared between the rows instead of being cut per row.
			const { visible } = applyPaywall(allMessages);
			const midIndex = Math.floor(visible.length / 2);

			data.push(
				{
					category: { name: '#Sale' },
					messages: visible.slice(0, midIndex),
				},
				{
					category: { name: '#Sale' },
					messages: visible.slice(midIndex),
				}
			);

			for (let categoryId of categoriesToShow) {
				const res = await sendNetworkReq({
					method: 'GET',
					url: `messages/category/${categoryId}?limit=100`,
				});
				if (!res?.error) data.push(res);
			}

			if (requestIdRef.current !== myRequestId) return; // stale
			setMessageSections(data);
		} catch (error) {
			console.error('🚨 Fetching messages failed:', error);
		}
	};
	const section0 = messageSections?.[0]?.messages || [];
	const section1 = messageSections?.[1]?.messages || [];
	const combined = [...section0, ...section1];

	// Only guests get the sign-in prompt here (after the messages they can see). Signed-in
	// users browse more elsewhere, so hasNextPage is true for them and no card shows.
	const showUpgrade = shouldShowPaywall({
		user,
		isPro,
		count: combined.length,
		hasNextPage: Boolean(user),
	});

	return (
		<>
			<CategoryCards categories={categories} />
			<BrandCards brands={brands} />
			{messageSections ? (
				<>
					<MessagesSlider
						title={messageSections?.[0]?.category?.name}
						messages={isMd ? combined : section0}
						paywalled={isMd && showUpgrade}
					/>
					{!isMd && (
						<MessagesSlider
							title={messageSections?.[1]?.category?.name}
							messages={section1}
							paywalled={showUpgrade}
						/>
					)}
				</>
			) : (
				<div style={{ minHeight: '300px' }} />
			)}
			<BrandSlider />
			<div className='flex justify-center pt-8 pb-4 min-h-[120px] w-full'>
				<div className='w-full min-h-[60px] max-w-[600px] md:w-1/2'>
					<SearchInput navigateToSearchPage />
				</div>
			</div>
		</>
	);
};

export default HomePage;
